import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Lock, Unlock, Sparkles, Loader2, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { stageArtUrl } from "@/lib/avatar/stageArt";
import { useAdminRole } from "@/hooks/useAdminRole";

export function MemberAvatarRepair({ memberId, name }: { memberId: string; name: string }) {
  const qc = useQueryClient();
  const { data: isAdmin } = useAdminRole();
  const [unlocked, setUnlocked] = useState(false);
  const [stage, setStage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<{ draftId: string; imageUrl: string } | null>(null);
  const invoke = async (action: "preview" | "accept") => {
    if (!unlocked) return;
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-avatar", {
        body: { action, targetUserId: memberId, stageNumber: stage, safetyUnlocked: unlocked, draftId: preview?.draftId,
          ...(isAdmin && stage >= 1 ? { progressionRestyle: true, styleReferenceUrl: stageArtUrl("male", stage) } : {}),
        },
      });
      if (error) {
        const response = (error as { context?: Response }).context;
        const detail = response ? await response.json().catch(() => null) : null;
        throw new Error(detail?.error || error.message);
      }
      if (data.error) throw new Error(data.error);
      if (action === "preview") setPreview(data);
      else {
        setPreview(null);
        toast.success(`Stage ${stage} personalized for ${name}`);
        qc.invalidateQueries({ queryKey: ["admin-member-panel", memberId] });
        qc.invalidateQueries({ queryKey: ["user-avatars", memberId] });
        if (stage < 8) setStage(stage + 1);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not repair avatar");
    } finally { setBusy(false); }
  };
  return <section className="space-y-3 border-t border-border pt-4">
    <h3 className="font-display text-lg text-foreground">Personalize member avatars</h3>
    <Button variant="outline" size="sm" disabled={busy} onClick={() => setUnlocked(!unlocked)}>
      {unlocked ? <Unlock className="mr-2 h-4 w-4" /> : <Lock className="mr-2 h-4 w-4" />}
      {unlocked ? "Safety lock off" : "Safety lock on"}
    </Button>
    <div className="flex flex-wrap gap-1" aria-label="Avatar repair stage">
      {Array.from({ length: 9 }, (_, n) => <Button key={n} variant={stage === n ? "default" : "outline"} size="sm" disabled={busy || !!preview} onClick={() => setStage(n)} aria-label={`Repair stage ${n}`}>{n}</Button>)}
    </div>
    {preview ? <>
      <img src={preview.imageUrl} alt={`${name} stage ${stage} preview`} className="mx-auto max-h-96 w-full object-contain" />
      <div className="flex flex-wrap gap-2">
        <Button disabled={busy || !unlocked} onClick={() => invoke("accept")}><Check className="mr-2 h-4 w-4" />Use this avatar</Button>
        <Button variant="outline" disabled={busy} onClick={() => setPreview(null)}>Discard preview</Button>
      </div>
    </> : <Button variant="outline" disabled={busy || !unlocked} onClick={() => invoke("preview")}>
      {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}Preview stage {stage} with member photo
    </Button>}
  </section>;
}